import { useState } from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import Card from '@/components/ui/Card';
import type { CategoryEntry } from './useWelcomeState';

interface Props {
  brandDescription: string;
  productType: string;
  categories: CategoryEntry[];
  excludeKeywords: string[];
  onChange: (partial: {
    brandDescription?: string;
    productType?: string;
    categories?: CategoryEntry[];
    excludeKeywords?: string[];
  }) => void;
}

export default function WelcomeStep3Brand({
  brandDescription,
  productType,
  categories,
  excludeKeywords,
  onChange,
}: Props) {
  const [newCategory, setNewCategory] = useState('');
  const [newExclusion, setNewExclusion] = useState('');

  const brandDescLen = brandDescription.trim().length;
  const hasExclusionClause = /we do not sell|we don't sell|NOT sell/i.test(brandDescription);
  const descWarning = brandDescLen < 150 || !hasExclusionClause;

  function addCategory() {
    const name = newCategory.trim();
    if (!name || categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return;
    onChange({ categories: [...categories, { name, priority: 'high' }] });
    setNewCategory('');
  }

  function removeCategory(name: string) {
    onChange({ categories: categories.filter((c) => c.name !== name) });
  }

  function addExclusion() {
    const kw = newExclusion.trim().toLowerCase();
    if (!kw || excludeKeywords.includes(kw)) return;
    onChange({ excludeKeywords: [...excludeKeywords, kw] });
    setNewExclusion('');
  }

  function removeExclusion(kw: string) {
    onChange({ excludeKeywords: excludeKeywords.filter((k) => k !== kw) });
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-secondary">
        Dippy uses this to filter out wrong-category suppliers. A detailed description with explicit
        exclusions dramatically improves prospect quality.
      </p>

      {/* Brand description */}
      <Card className="p-5">
        <label className="block text-sm font-semibold text-text-primary mb-2">
          What does your store sell?
        </label>
        <p className="text-xs text-text-secondary mb-3">
          Include primary categories, explicit exclusions ("We do NOT sell X, Y, Z"), and what
          suppliers must actually stock.
        </p>
        <textarea
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm min-h-[140px] font-mono focus:outline-none focus:ring-2 focus:ring-sage-500"
          value={brandDescription}
          onChange={(e) => onChange({ brandDescription: e.target.value })}
          placeholder="Example: LaSalle Cat Tree — an online specialty retailer of cat supplies. SIGNATURE CATEGORIES: cat trees and automatic litter boxes. We do NOT sell dog supplies, cat food, or cat-themed novelties..."
        />
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-text-muted">{brandDescLen} characters</span>
          {descWarning && brandDescLen > 0 && (
            <span className="flex items-center gap-1 text-amber-700">
              <AlertTriangle className="w-3.5 h-3.5" />
              {brandDescLen < 150
                ? 'Too short — aim for 150+ characters'
                : 'Add "We do NOT sell..." exclusions for better results'}
            </span>
          )}
        </div>
      </Card>

      {/* Product type */}
      <Card className="p-5">
        <label className="block text-sm font-semibold text-text-primary mb-2">
          Product type (short form)
        </label>
        <p className="text-xs text-text-secondary mb-3">
          One-line label Dippy uses in searches and supplier outreach.
        </p>
        <input
          type="text"
          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
          value={productType}
          onChange={(e) => onChange({ productType: e.target.value })}
          placeholder="e.g. cat supplies with focus on cat trees and automatic litter boxes"
          maxLength={500}
        />
      </Card>

      {/* Categories */}
      <Card className="p-5">
        <label className="block text-sm font-semibold text-text-primary mb-2">
          Priority categories {categories.length > 0 && <span className="text-text-muted font-normal">({categories.length})</span>}
        </label>
        <p className="text-xs text-text-secondary mb-3">
          Top 3–5 categories Dippy should hunt for first. Press Enter to add.
        </p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCategory();
              }
            }}
            placeholder="e.g. automatic litter boxes"
          />
          <button
            type="button"
            onClick={addCategory}
            className="px-3 py-2 bg-sage-600 text-white rounded-lg text-sm hover:bg-sage-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <span
              key={cat.name}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-100 text-sage-800 text-sm rounded-full"
            >
              {cat.name}
              <button
                type="button"
                onClick={() => removeCategory(cat.name)}
                className="hover:bg-sage-200 rounded-full p-0.5"
                aria-label={`Remove ${cat.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {categories.length === 0 && (
            <span className="text-xs text-text-muted italic">No categories yet — add at least one to continue.</span>
          )}
        </div>
      </Card>

      {/* Exclude keywords */}
      <Card className="p-5">
        <label className="block text-sm font-semibold text-text-primary mb-2">
          Exclude keywords <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <p className="text-xs text-text-secondary mb-3">
          Skip suppliers whose focus includes these words (e.g. "dog", "food", "supplements").
        </p>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            value={newExclusion}
            onChange={(e) => setNewExclusion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExclusion();
              }
            }}
            placeholder="e.g. dog"
          />
          <button
            type="button"
            onClick={addExclusion}
            className="px-3 py-2 bg-stone-600 text-white rounded-lg text-sm hover:bg-stone-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {excludeKeywords.map((kw) => (
            <span
              key={kw}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 text-stone-700 text-sm rounded-full"
            >
              {kw}
              <button
                type="button"
                onClick={() => removeExclusion(kw)}
                className="hover:bg-stone-200 rounded-full p-0.5"
                aria-label={`Remove ${kw}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
}
